import React from 'react';

import Box from '@mui/material/Box';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';

export type FlowFileItem = {
  name: string;
  action: string;
  note?: string;
};

export type FlowRuleItem = {
  name: string;
  trigger: string;
};

type FlowStepProps = {
  step: string;
  icon: React.ReactNode;
  title: string;
  desc: string;
  files: FlowFileItem[];
  rules: FlowRuleItem[];
  ruleNote?: string;
};

/**
 * フローステップ1つ分（ファイル操作 + ルール参照付き）
 */
export const WorkflowFlowStep: React.FC<FlowStepProps> = ({
  step,
  icon,
  title,
  desc,
  files,
  rules,
  ruleNote,
}) => (
  <Box
    sx={{
      p: 2,
      border: 1,
      borderColor: 'divider',
      borderRadius: 2,
      bgcolor: 'background.paper',
    }}
  >
    {/* ヘッダー */}
    <Stack direction="row" spacing={1.5} alignItems="flex-start">
      <Box
        sx={{
          minWidth: 40,
          height: 40,
          borderRadius: '50%',
          bgcolor: 'primary.main',
          color: '#fff',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          flexShrink: 0,
        }}
      >
        {icon}
      </Box>
      <Box sx={{ flex: 1 }}>
        <Typography
          variant="caption"
          fontWeight={700}
          sx={{
            color: 'primary.main',
            bgcolor: (theme) =>
              theme.palette.mode === 'dark'
                ? 'rgba(25, 118, 210, 0.15)'
                : 'rgba(25, 118, 210, 0.1)',
            px: 0.75,
            py: 0.1,
            borderRadius: 0.5,
            display: 'inline-block',
            mb: 0.5,
          }}
        >
          {step}
        </Typography>
        <Typography variant="body2" fontWeight={600}>
          {title}
        </Typography>
        <Typography variant="caption" color="text.secondary">
          {desc}
        </Typography>
      </Box>
    </Stack>

    {/* ファイル操作 + ルール参照 */}
    <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5} sx={{ mt: 1.5, pl: 6.5 }}>
      {/* ファイル */}
      <Box
        sx={{
          flex: 1,
          border: 1,
          borderColor: 'divider',
          borderRadius: 1.5,
          p: 1,
          bgcolor: (theme) =>
            theme.palette.mode === 'dark' ? 'rgba(46, 125, 50, 0.06)' : 'rgba(46, 125, 50, 0.04)',
        }}
      >
        <Typography
          variant="caption"
          fontWeight={700}
          sx={{ color: '#2e7d32', display: 'block', mb: 0.5 }}
        >
          ファイル操作
        </Typography>
        {files.map((f, i) => (
          <Box key={i} sx={{ mb: 0.5 }}>
            <Typography variant="caption" fontWeight={600}>
              {f.name}
            </Typography>
            <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
              [{f.action}]{f.note ? ` ${f.note}` : ''}
            </Typography>
          </Box>
        ))}
      </Box>

      {/* ルール */}
      <Box
        sx={{
          flex: 1,
          border: 1,
          borderColor: 'divider',
          borderRadius: 1.5,
          p: 1,
          bgcolor: (theme) =>
            theme.palette.mode === 'dark' ? 'rgba(25, 118, 210, 0.06)' : 'rgba(25, 118, 210, 0.04)',
        }}
      >
        <Typography
          variant="caption"
          fontWeight={700}
          sx={{ color: '#1565c0', display: 'block', mb: 0.5 }}
        >
          参照されるルール
        </Typography>
        {rules.length > 0 ? (
          rules.map((r, i) => (
            <Box key={i} sx={{ mb: 0.5 }}>
              <Typography variant="caption" fontWeight={600}>
                {r.name}
              </Typography>
              <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                ({r.trigger})
              </Typography>
            </Box>
          ))
        ) : (
          <Typography variant="caption" color="text.secondary">
            —（なし）
          </Typography>
        )}
        {ruleNote && (
          <Typography
            variant="caption"
            color="text.secondary"
            sx={{ mt: 0.5, display: 'block', fontStyle: 'italic' }}
          >
            {ruleNote}
          </Typography>
        )}
      </Box>
    </Stack>
  </Box>
);

type FlowArrowProps = {
  label: string;
};

/**
 * ステップ間の矢印
 */
export const WorkflowFlowArrow: React.FC<FlowArrowProps> = ({ label }) => (
  <Stack alignItems="center" sx={{ py: 1 }}>
    <Box
      sx={{
        width: '2px',
        height: 12,
        bgcolor: 'text.disabled',
      }}
    />
    <Typography
      variant="caption"
      color="text.secondary"
      sx={{
        px: 1.5,
        py: 0.25,
        border: 1,
        borderColor: 'divider',
        borderRadius: 1,
        bgcolor: 'action.hover',
        textAlign: 'center',
        fontStyle: 'italic',
      }}
    >
      {label}
    </Typography>
    <Box
      sx={{
        width: '2px',
        height: 12,
        bgcolor: 'text.disabled',
      }}
    />
    <Box
      sx={{
        width: 0,
        height: 0,
        borderLeft: '5px solid transparent',
        borderRight: '5px solid transparent',
        borderTop: '6px solid',
        borderTopColor: 'text.disabled',
      }}
    />
  </Stack>
);
